import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  createRoleApi,
  deleteRoleApi,
  getAllRolesApi,
  getAllUserAuthApisApi,
  getMemberRulesApi,
  getPermissionsGroupsApi,
  getRoleApi,
  updateMemberRolesApi,
  updateRoleApi,
  updateRoleRulesApi,
} from '~/api/role';
import type {
  CreateRole,
  PermissionsGroupRawData,
  PermissionsGroupViewData,
  PermissionsRawData,
  PermissionsTagViewData,
  Role,
  UserAuthApi,
} from '~/types/role';

const ROLE_TTL_MS = 60 * 60 * 1000;

export const useRoleStore = defineStore('role', () => {
  const allUserAuthApis = ref<UserAuthApi[]>([]);
  const roles = ref<Role[]>([]);
  const rules = ref<string[]>([]);
  const permissionsGroupsRawData = ref<PermissionsGroupRawData[]>([]);
  const permissionsGroupViewData = ref<PermissionsGroupViewData[]>([]);
  const loading = ref(false);
  const rolesLastFetchedAt = ref<number | null>(null);
  const rulesLastFetchedAt = ref<number | null>(null);
  const permissionsLastFetchedAt = ref<number | null>(null);

  // apiGroups = [
  //   {
  //     "id": 1,
  //     "group_name": "User & Role Management",
  //     "permissions": [
  //       {
  //         "id": 1,
  //         "api_group_id": 1,
  //         "permissions_key": "role_admin",
  //         "tag": "role",
  //         "api_id": 3
  //       },
  //       {
  //         "id": 16,
  //         "api_group_id": 1,
  //         "permissions_key": "role_delete",
  //         "tag": "role",
  //         "api_id": 18
  //       },
  //       {
  //         "id": 12,
  //         "api_group_id": 1,
  //         "permissions_key": "role_editor",
  //         "tag": "role",
  //         "api_id": 3
  //       },
  //       {
  //         "id": 9,
  //         "api_group_id": 1,
  //         "permissions_key": "role_viewer",
  //         "tag": "role",
  //         "api_id": 177
  //       },
  //       {
  //         "id": 11,
  //         "api_group_id": 1,
  //         "permissions_key": "role_writer",
  //         "tag": "role",
  //         "api_id": 107
  //       },
  //       {
  //         "id": 11,
  //         "api_group_id": 1,
  //         "permissions_key": "user_admin",
  //         "tag": "user",
  //         "api_id": 199
  //       },
  //       {
  //         "id": 11,
  //         "api_group_id": 1,
  //         "permissions_key": "user_writer",
  //         "tag": "user",
  //         "api_id": 201
  //       }
  //     ]
  //   },
  //   {
  //     "id": 2,
  //     "group_name": "Organization",
  //     "permissions": [
  //       {
  //         "id": 17,
  //         "api_group_id": 2,
  //         "permissions_key": "org_admin",
  //         "tag": "",
  //         "api_id": 38
  //       },
  //       {
  //         "id": 20,
  //         "api_group_id": 2,
  //         "permissions_key": "org_writer",
  //         "tag": "",
  //         "api_id": 38
  //       }
  //     ]
  //   }
  // ]

  // 資料轉換函數：將扁平化的 API 資料轉換為分組結構
  function transformApiDataToViewData(
    permissionsGroupsRawData: PermissionsGroupRawData[]
  ): PermissionsGroupViewData[] {
    const permissionsGroupViewData: PermissionsGroupViewData[] = [];

    for (const group of permissionsGroupsRawData) {
      // 按 tag 將權限進行分組
      const tagGroupMap = new Map<string, PermissionsRawData[]>();

      for (const permission of group.permissions) {
        const tag = permission.tag || ''; // 處理空 tag 的情況
        if (!tagGroupMap.has(tag)) {
          tagGroupMap.set(tag, []);
        }
        const permissions = tagGroupMap.get(tag);
        if (permissions) {
          permissions.push(permission);
        }
      }

      // 將分組後的資料轉換為 PermissionsTagViewData 格式
      const tags: PermissionsTagViewData[] = [];
      for (const [tag, permissions] of tagGroupMap.entries()) {
        tags.push({
          tag,
          permissions,
        });
      }

      // 建立該群組的 ViewData 物件
      const groupViewData: PermissionsGroupViewData = {
        id: group.id,
        group_name: group.group_name,
        tags,
      };

      permissionsGroupViewData.push(groupViewData);
    }
    return permissionsGroupViewData;
  }

  async function getAllRoles(options?: { force?: boolean }) {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && roles.value.length > 0 && rolesLastFetchedAt.value) {
      const isFresh = now - rolesLastFetchedAt.value <= ROLE_TTL_MS;
      if (isFresh) {
        return roles.value;
      }
    }
    roles.value = await getAllRolesApi();
    rolesLastFetchedAt.value = Date.now();
    return roles.value;
  }

  async function fetchAllRoles() {
    roles.value = await getAllRolesApi();
    rolesLastFetchedAt.value = Date.now();
    return roles.value;
  }

  async function updateMemberRoles(params: { uid: number; id: number }, role_uuids: string[]) {
    await updateMemberRolesApi(params, role_uuids);
  }

  async function getMemberRules(options?: { force?: boolean }) {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && rules.value.length > 0 && rulesLastFetchedAt.value) {
      const isFresh = now - rulesLastFetchedAt.value <= ROLE_TTL_MS;
      if (isFresh) {
        return rules.value;
      }
    }
    rules.value = await fetchMemberRules();
    return rules.value;
  }

  async function fetchMemberRules() {
    rules.value = await getMemberRulesApi();
    rulesLastFetchedAt.value = Date.now();
    return rules.value;
  }

  async function createRole(role: CreateRole) {
    const createRoleData = {
      name: role.name,
      description: role.description,
      bg_color: role.bg_color,
      text_color: role.text_color,
      // selectedRules: role.selectedRules
    };
    const newRoleId = await createRoleApi(createRoleData);
    // await fetchAllRoles()
    // const newRole = roles.value.find(r => r.id === newRoleId)
    // if (!newRole) {
    //   throw new Error('unexpected error')
    // }
    // if (role.selectedRules) {
    //   const canUpdateRules = await usePermission().userRole.canUpdateRules()
    //   if (canUpdateRules) {
    //     await updateRoleRulesApi(newRole.role_uuid, role.selectedRules)
    //   }
    // }
    return newRoleId;
  }

  async function updateRole(uuid: string, role: CreateRole) {
    const originalRole = roles.value.find((r) => r.role_uuid === uuid);
    if (!originalRole) {
      throw new Error('Role not found');
    }
    const updateRole = { ...originalRole } as Role;
    updateRole.name = role.name;
    updateRole.description = role.description;
    updateRole.bg_color = role.bg_color;
    updateRole.text_color = role.text_color;
    await updateRoleApi(updateRole);
  }

  async function updateRoleRules(uuid: string, permissions_keys: string[]) {
    await updateRoleRulesApi(uuid, permissions_keys);
  }

  async function deleteRole(id: number) {
    await deleteRoleApi(id);
  }

  async function getAllUserAuthApis(options?: { force?: boolean }) {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && allUserAuthApis.value.length > 0 && permissionsLastFetchedAt.value) {
      const isFresh = now - permissionsLastFetchedAt.value <= ROLE_TTL_MS;
      if (isFresh) {
        return allUserAuthApis.value;
      }
    }
    allUserAuthApis.value = await getAllUserAuthApisApi();
    permissionsLastFetchedAt.value = Date.now();
    return allUserAuthApis.value;
  }

  async function getPermissionsGroupsRawData(options?: { force?: boolean }) {
    const force = options?.force === true;
    const now = Date.now();
    if (!force && permissionsGroupsRawData.value.length > 0 && permissionsLastFetchedAt.value) {
      const isFresh = now - permissionsLastFetchedAt.value <= ROLE_TTL_MS;
      if (isFresh) {
        return permissionsGroupsRawData.value;
      }
    }
    permissionsGroupsRawData.value = await getPermissionsGroupsApi();
    permissionsLastFetchedAt.value = Date.now();
    // 同時更新分組資料
    permissionsGroupViewData.value = transformApiDataToViewData(permissionsGroupsRawData.value);
    return permissionsGroupsRawData.value;
  }

  // 新增取得分組資料的函數
  async function getPermissionsGroupsViewData(options?: { force?: boolean }) {
    if (
      !options?.force &&
      permissionsGroupViewData.value.length > 0 &&
      permissionsLastFetchedAt.value
    ) {
      const now = Date.now();
      const isFresh = now - permissionsLastFetchedAt.value <= ROLE_TTL_MS;
      if (isFresh) {
        return permissionsGroupViewData.value;
      }
    }
    await getPermissionsGroupsRawData({ force: options?.force });
    return permissionsGroupViewData.value;
  }

  // 強制重新取得資料的函數
  async function fetchPermissionsGroupsViewData() {
    permissionsGroupsRawData.value = await getPermissionsGroupsApi();
    permissionsGroupViewData.value = transformApiDataToViewData(permissionsGroupsRawData.value);
    permissionsLastFetchedAt.value = Date.now();
    return permissionsGroupViewData.value;
  }

  async function getRole(role_uuid: string) {
    return await getRoleApi(role_uuid);
  }

  const reset = () => {
    allUserAuthApis.value = [];
    roles.value = [];
    rules.value = [];
    permissionsGroupsRawData.value = [];
    permissionsGroupViewData.value = [];
    loading.value = false;
    rolesLastFetchedAt.value = null;
    rulesLastFetchedAt.value = null;
    permissionsLastFetchedAt.value = null;
  };

  return {
    roles,
    rules,
    permissionsGroupsRawData,
    permissionsGroupViewData,
    allUserAuthApis,
    loading,
    getAllRoles,
    fetchAllRoles,
    updateMemberRoles,
    getMemberRules,
    fetchMemberRules,
    createRole,
    updateRole,
    updateRoleRules,
    deleteRole,
    getAllUserAuthApis,
    getPermissionsGroupsRawData,
    getPermissionsGroupsViewData,
    fetchPermissionsGroupsViewData,
    getRole,
    reset,
  };
});
